/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import Text from '../Text'
import colors from '@sportninja/common/constants/appColors'
import { Select } from 'antd'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { paragraphSmall } from 'src/components/css'
import { ErrorLabel } from '../ErrorLabel'

type SelectMultiOption = { label: string; value: string }

/** A titled group of options (antd renders the label as a group header). */
type SelectMultiOptionGroup = { label: string; options: SelectMultiOption[] }

type SelectMultiInputProps = {
  label: string
  options: (SelectMultiOption | SelectMultiOptionGroup)[]
  value: string[]
  onChange: (value: string[]) => void
  /**
   * Either shape: the legacy array of errored field NAMES, or the map of
   * `{ field: message }` the API-driven forms use. Both were already handled
   * at runtime (here and in `ErrorLabel`) — only this type was narrower than
   * the component.
   */
  fieldErrors: string[] | Record<string, string>
  customError: any
  name: string
  /** Shown when nothing is selected. */
  placeholder?: string
  /**
   * Pin the control to the standard 40px so it lines up with the single-select
   * and text fields beside it, instead of growing as tags wrap.
   *
   * Opt-in, and off by default: a fixed height means a long selection overflows
   * horizontally rather than wrapping to a second row. Only pass it where the
   * control shares a row with 40px fields AND selections are short — a filter
   * bar, not a form field that can hold many long values.
   */
  fixedHeight?: boolean
  /**
   * Read-only: the selection is shown but cannot change (e.g. positions owned
   * elsewhere). Optional — every existing caller stays editable.
   */
  disabled?: boolean
}

const SelectMultiInput = ({
  label,
  options,
  value,
  onChange,
  fieldErrors,
  customError,
  name,
  placeholder,
  fixedHeight = false,
  disabled = false,
}: SelectMultiInputProps) => {
  // Array shape lists errored field NAMES; map shape keys messages by field.
  const hasFieldError = Array.isArray(fieldErrors)
    ? fieldErrors.includes(name)
    : Boolean(fieldErrors?.[name])

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        width: 100%;
      `}
    >
      {label && (
        <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
          {label}
        </Text>
      )}
      <Select
        mode='multiple'
        /*
         * A pinned-height control cannot wrap, so overflow has to collapse
         * rather than spill: 'responsive' keeps as many tags as fit on the one
         * row and folds the rest into a "+N" counter. Without it a fourth tag
         * wrapped to a second row and overlapped the field below.
         */
        maxTagCount={fixedHeight ? 'responsive' : undefined}
        placeholder={placeholder}
        options={options}
        value={value}
        onChange={onChange}
        disabled={disabled}
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
        showSearch={true}
        getPopupContainer={(triggerNode) =>
          triggerNode.parentElement || document.body
        }
        css={css`
          width: 100%;

          .ant-select-selector {
            padding: ${fixedHeight ? '0 8px' : '4px 8px'} !important;
            background-color: ${colors.SECONDARY_200}!important;
            border-radius: 8px;
            ${fixedHeight
              ? /* Fill the root rather than setting a floor of its own. */
                'height: 100% !important; min-height: 0 !important;'
              : /* Default: grow with the tags, floored at the 40px control
                   height so an empty control still matches its neighbours. */
                'min-height: 40px !important;'}
          }

          /*
           * The 40px goes on the ROOT, exactly as SelectInput does it. Setting
           * it on the inner .ant-select-selector alone (as a min-height) left
           * the root free to grow, so antd's own multiple-mode selector
           * min-height plus the tag row's margins pushed the control taller
           * than every other field on the row.
           */
          ${fixedHeight &&
          `
            height: 40px !important;

            .ant-select-selection-overflow {
              height: 100%;
            }
            .ant-select-selection-overflow-item {
              align-self: center;
            }
          `}
          /* The "+N" overflow chip is a selection item too — keep it inside
             the row rather than letting it set its own height. */
          .ant-select-selection-overflow-item-rest .ant-select-selection-item {
            ${fixedHeight && 'height: 24px; line-height: 22px;'}
          }
          .ant-select-selection-item-content {
            ${paragraphSmall}
            align-self: center;
            /* The tag is a colors.PRIMARY fill, so its content colour is
               tenant-switched — SECONDARY_300 was dark-on-blue on Canlan. */
            color: ${PRIMARY_BUTTON_CONTENT.text};
          }
          .ant-select-selection-item {
            background-color: ${colors.PRIMARY}!important;
            ${fixedHeight
              ? /* A tag has to fit INSIDE the 40px control, not set its
                   height. 24px is the 32px small-control height less the
                   selector's padding, and antd's default top/bottom tag margin
                   would re-add the height the root just fixed. */
                `
                  height: 24px;
                  line-height: 22px;
                  padding: 0 8px !important;
                  margin-top: 0 !important;
                  margin-bottom: 0 !important;
                  display: inline-flex;
                  align-items: center;
                `
              : 'padding: 4px 8px !important;'}
          }
          .ant-select-selection-item-remove {
            color: ${PRIMARY_BUTTON_CONTENT.icon}!important;
          }
        `}
        status={hasFieldError ? 'error' : ''}
      />
      <ErrorLabel
        fieldErrors={fieldErrors || []}
        customError={customError || ''}
        name={name}
      />
    </div>
  )
}

export default SelectMultiInput
